/**
 * Design it for me.
 *
 * A homeowner types their room, locks the things that cannot move -- the sink is where the
 * plumbing is, the oven where the gas is -- and asks for a kitchen. What comes back is three
 * kitchens a designer would have drawn, each valid by construction, each explained.
 *
 * This is a GENERATOR, not a search: a designer does not try every arrangement, they place
 * the things that matter in the order that matters and fill in around them. So: the sink
 * goes first (locked, else the middle of the longest wall), the dishwasher beside it, the
 * cooktop across a stretch of bench from it, the fridge at the end of the rail so the sink
 * sits between fridge and cooktop, a corner unit in every corner, and the rest is drawers or
 * cupboards according to taste. Three tastes, a couple of variants each, scored by the same
 * rules the review applies, and the best of each taste is offered.
 *
 * The shape of `design()`'s answer is the contract. A model can produce it later; the sheet
 * that shows options neither knows nor cares which did.
 *
 * Pure. Deterministic for a seed. No three.js, no Vue.
 */
import { compose, fill, sum } from './layout.js'
import { review, RULE } from './guidelines.js'
import * as room from './room.js'
import { SIZE } from './appliance.js'

/** Three ways to fill a kitchen. The names are what the homeowner sees. */
export const MIXES = [
  { id: 'drawers', name: "Cook's kitchen", blurb: 'Drawers where the pans and the plates live' },
  { id: 'balanced', name: 'Everyday', blurb: 'Drawers where they earn it, cupboards elsewhere' },
  { id: 'doors', name: 'Classic', blurb: 'Cupboards, and a pantry beside the fridge' },
]

/** Roles a homeowner may lock in place. Everything else is the generator's to arrange. */
export const LOCKABLE = ['sink', 'cooktop', 'oven', 'fridge', 'dishwasher']

/**
 * @returns {{ options: Array<{ id, name, blurb, why: string[], plan, score }> }}
 */
export function design(plan, maker, opts = {}) {
  const seed = Math.max(1, Math.floor(opts.seed ?? 1))
  const count = opts.count ?? 3
  const lim = maker.limits
  const specs = maker.cabinets
  const locks = plan.locks ?? []

  // The bones: walls and which walls carry runs. Contents are what we are here to decide.
  const bones = {
    walls: plan.walls,
    // The doorways and windows come with it: `extents` shortens a run for one at either end
    // of a wall, and `openingsOn` below steps the cabinets over one in the middle. Left out,
    // the generator cheerfully draws a run of cupboards across the door.
    openings: plan.openings ?? [],
    islands: plan.islands ?? [],
    runs: plan.runs.map((r) => ({ id: r.id, wallId: r.wallId, kind: r.kind, items: [] })),
  }
  const ext = room.extents(bones, lim)
  for (const r of bones.runs) Object.assign(r, ext[r.id])

  const baseChains = room.chains(bones).filter((c) => c.kind === 'base')
  if (!baseChains.length) return { options: [], rejected: [], impossible: [] }

  const rails = baseChains.map((c) => rail(c))

  // Where a locked cabinet is now, on whichever rail it is on.
  const fixedByLock = lockedPositions(plan, rails, locks, lim)

  // The main rail: the one holding a locked sink, else the longest.
  const main = rails.find((r) => fixedByLock.some((f) => f.role === 'sink' && f.railId === r.id))
    ?? rails.slice().sort((a, b) => b.length - a.length)[0]

  // Every variant tried, and why the ones that failed failed -- so the sheet can say "nothing
  // fits with the sink there" instead of showing an empty page.
  const variants = []
  const rejected = []
  // Locks that no arrangement can honour, named. The whole point of saying so is that "the
  // dishwasher is standing in the doorway" is a thing a person can act on and "nothing fits"
  // is not.
  const impossible = []
  for (const mix of MIXES) {
    for (const dishSide of ['left', 'right']) {
      for (const tower of [true, false]) {
        const built = build({ bones, rails, main, mix, dishSide, tower, fixedByLock, specs, lim, locks })
        if (built.error) {
          for (const i of built.impossible ?? []) {
            if (!impossible.some((x) => x.itemId === i.itemId)) impossible.push(i)
          }
          rejected.push({ mix: mix.id, dishSide, tower, reason: built.error }); continue
        }
        const scored = score(built.plan, maker, built.notes)
        if (scored.faults.length) {
          rejected.push({ mix: mix.id, dishSide, tower, reason: scored.faults.map((f) => f.text).join(' ') })
          continue
        }
        variants.push({ mix, dishSide, tower, ...built, ...scored })
      }
    }
  }
  if (!variants.length) return { options: [], rejected, impossible }

  // Best of each taste, so three options are three DIFFERENT kitchens rather than the same
  // one three ways. `seed` walks down each taste's list, which is what "show me others" does.
  const options = []
  for (const mix of MIXES) {
    const ofMix = variants.filter((v) => v.mix.id === mix.id).sort((a, b) => b.score - a.score)
    if (!ofMix.length) continue
    const pick = ofMix[(seed - 1) % ofMix.length]
    options.push({
      id: `${mix.id}-${pick.dishSide}-${pick.tower ? 'tower' : 'flat'}`,
      name: mix.name,
      blurb: mix.blurb,
      why: pick.why,
      plan: pick.plan,
      score: pick.score,
    })
  }
  return { options: options.slice(0, count), rejected, impossible }
}

// ── rails ──────────────────────────────────────────────────────────────────────────

/** A chain as one line: legs laid end to end, positions in mm from the rail's start. */
function rail(chain) {
  let at = 0
  const legs = chain.legs.map((run) => {
    const len = Math.max(0, run.to - run.from)
    const leg = { run, start: at, end: at + len, len }
    at += len
    return leg
  })
  return { id: chain.legs[0].id, chain, legs, length: at }
}

/** Which leg a rail position is on, and how far along it. */
const legAt = (r, pos) => r.legs.find((l) => pos >= l.start - 0.5 && pos < l.end + 0.5) ?? r.legs[r.legs.length - 1]

/**
 * Rail positions of the cabinets a homeowner has locked, from where they stand today.
 *
 * A lock means one thing: this cabinet does not move along its WALL. So the position is
 * worked out in the wall's own millimetres -- where the run it is in starts today, plus the
 * widths of everything before it -- and only then converted onto the rail the generator is
 * about to fill, by the same `leg.start - run.from` conversion `build` uses for a doorway.
 *
 * IT USED TO ADD UP LEG LENGTHS INSTEAD, on a room that was missing two things.
 *
 *   `cur` was `{ walls, runs }` with no `openings` and no `islands`. Without the openings,
 *   `extents` did not shorten a run for a doorway at the end of its wall, so every lock on
 *   that rail was reported further along than it stands. Without the islands, a run on an
 *   island resolved to no segment at all: `extents` answered {0,0} and `chains` left it out
 *   entirely, so a lock on the island was silently dropped and the island's sink moved.
 *
 *   And the rail it measured was built from the CURRENT runs, which carry the `span` a scan
 *   measured, where the rail being filled is built from `bones`, which do not. Two rails of
 *   different lengths, one position: the lock landed wherever the difference put it.
 *
 * Working in wall millimetres has none of those three failure modes, because a wall is the
 * one frame both the old runs and the new ones agree about.
 */
function lockedPositions(plan, rails, locks, lim) {
  const out = []
  if (!locks.length) return out
  const cur = { walls: plan.walls, islands: plan.islands ?? [], openings: plan.openings ?? [],
    runs: plan.runs }
  const curExt = room.extents(cur, lim)

  // Where every item in the room stands along its own wall, today.
  const where = new Map()
  for (const run of plan.runs) {
    const e = curExt[run.id] ?? { from: 0, to: 0 }
    let off = 0
    for (const it of run.items ?? []) {
      where.set(it.id, { wallId: run.wallId, kind: run.kind, at: e.from + off, item: it })
      off += it.w
    }
  }

  for (const lock of locks) {
    const w = where.get(lock.itemId)
    if (!w) continue
    for (const r of rails) {
      const leg = r.legs.find((l) => l.run.wallId === w.wallId && l.run.kind === w.kind)
      if (!leg) continue
      out.push({ role: lock.role ?? null, itemId: lock.itemId, specId: w.item.specId, w: w.item.w,
        railId: r.id, pos: leg.start + (w.at - leg.run.from), wallId: w.wallId })
    }
  }
  return out
}

// ── placing ────────────────────────────────────────────────────────────────────────

function build({ bones, rails, main, mix, dishSide, tower, fixedByLock, specs, lim }) {
  const notes = []
  const byRole = (role, kind) => specs.find((s) => s.role === role && (!kind || s.kind === kind))
  /*
   * The space nearest the appliance that goes in it.
   *
   * A range that sells a fridge space in four widths made this a lottery decided by list
   * order -- `byRole` takes the first match, so moving a line in maker.js changed every
   * generated kitchen from a 900 fridge to a 600 one. The size of the appliance is the
   * thing that should decide, and appliance.js already knows it.
   */
  const bestFor = (role, kind) => {
    const want = SIZE[role]?.w
    const all = specs.filter((s) => s.role === role && (!kind || s.kind === kind))
    if (!want || !all.length) return byRole(role, kind)
    return all.slice().sort((a, b) => Math.abs(a.w - want) - Math.abs(b.w - want))[0]
  }
  const S = {
    sink: byRole('sink', 'base'),
    dishwasher: byRole('dishwasher', 'base'),
    cooktop: byRole('cooktop', 'base'),
    fridge: bestFor('fridge', 'tall'),
    oven: byRole('oven', 'tall'),
    corner: byRole('corner', 'base'),
    wallCorner: byRole('corner', 'wall'),
    hood: byRole('rangehood', 'wall'),
    pantry: specs.find((s) => s.kind === 'tall' && !s.role && /pantry/i.test(s.name)),
  }

  // Fixed things on each rail, as { role, specId, w, pos, id? }. Locks first, verbatim.
  const fixed = new Map(rails.map((r) => [r.id, []]))
  for (const f of fixedByLock) fixed.get(f.railId)?.push({ ...f })

  /*
   * A doorway is a fixed thing that takes width and holds no cabinet.
   *
   * Put in here, before anything is placed, it is avoided by everything that follows for
   * free: `findSpot` already refuses to overlap what is in this list, so the sink does not
   * land across the door, and `fillLeg` fills around it as it fills around a locked
   * cabinet. The first attempt gave it to fillLeg alone, and the sink was placed on top of
   * it -- two fixed things claiming the same 870mm, and a leg that added up to exactly one
   * doorway too much.
   *
   * `extents` has already dealt with an opening at either END of a wall by shortening the
   * run, so what reaches here is the one in the middle. `itemId` is fillLeg's own flag for
   * "never move this", which is true of a door in a way it is not of a cabinet.
   *
   * An opening's `at` is measured from its WALL's start; a rail's coordinates run from where
   * its first run begins and continue through the corner. `leg.start - run.from` is the
   * conversion, and getting it wrong puts the gap on the neighbouring wall.
   */
  for (const r of rails) {
    for (const leg of r.legs) {
      const shift = leg.start - leg.run.from
      for (const o of room.blockedSpans(bones, leg.run.wallId, leg.run.kind, lim)) {
        const from = Math.max(o.from + shift, leg.start)
        const to = Math.min(o.to + shift, leg.end)
        if (to - from < 1) continue
        fixed.get(r.id).push({ itemId: o.ids[0], specId: 'gap', gap: true, pos: from, w: to - from,
          // Bench beside a cooktop is the tightest rule that meets a doorway, so keep
          // everything that gets PLACED that far off one. The gap itself is still exactly
          // the width of the opening; this only steers the sink and the cooktop away.
          clear: o.kind === 'door' ? RULE.cooktopSide : 0 })
      }
    }
  }

  /*
   * NOTHING FIXED MAY STAND ON ANYTHING ELSE FIXED.
   *
   * Two things get into `fixed` before a single cabinet is chosen: the locks, which come
   * from where a scan measured an appliance, and the doorways, which come from where a scan
   * measured an opening. They are two separate readings of the same wall by the same phone
   * and they are allowed to disagree -- a wide cased opening is read off a wall it does not
   * quite line up with, a benchtop is measured a little past it, and the dishwasher ends up
   * nominally inside the doorway.
   *
   * Left alone, `fillLeg` lays both out at their full widths and the leg adds up to one
   * doorway more than the wall has ("adds up to 7283 on a 6433 leg"), and every one of the
   * twelve variants is rejected for the same arithmetic. The sheet then says nothing fits
   * and the room is left empty, which is what the owner saw.
   *
   * The doorway wins, because its width and position are the building's and not ours. The
   * lock steps to the nearest place it fits on its own leg -- it is still on its wall, still
   * near its plumbing, and `moved` says by how much so the option can own up to it. A lock
   * with nowhere to go at all is reported by name rather than turned into arithmetic.
   */
  const impossible = []
  const moved = []
  for (const r of rails) {
    const list = fixed.get(r.id)
    const doors = list.filter((f) => f.gap)
    // `clear` is elbow room for a cooktop that has a CHOICE about where it stands. A lock
    // has none, so it is asked only not to overlap: shoving a locked sink 300mm off its
    // plumbing to be tidy about the doorway is the opposite of what a lock is for.
    const hard = doors.map((f) => ({ ...f, clear: 0 }))
    const cabs = list.filter((f) => !f.gap).sort((a, b) => a.pos - b.pos)
    const settled = []
    for (const c of cabs) {
      const spot = findSpot(r, [...hard, ...settled], c.w, c.pos, 0)
      if (spot == null) { impossible.push({ role: c.role, itemId: c.itemId }); continue }
      if (Math.abs(spot - c.pos) > 0.5) moved.push({ role: c.role, by: Math.round(Math.abs(spot - c.pos)) })
      c.pos = spot
      settled.push(c)
    }
    fixed.set(r.id, [...doors, ...settled])
  }
  if (impossible.length) {
    return { error: `${impossible.map((i) => i.role ?? 'a locked cabinet').join(' and ')} `
      + 'cannot stay where it is: the doorway is there', impossible }
  }
  for (const m of moved) {
    notes.push(`The ${m.role ?? 'locked cabinet'} moved ${m.by}mm to clear the doorway`)
  }

  const has = (railId, role) => fixed.get(railId).some((f) => f.role === role)
  const place = (r, item, want, { fromEnd = 0, mustFit = true, exact = false } = {}) => {
    /*
     * On one leg, clear of the ends by `fromEnd`, and not on top of anything already there.
     *
     * `exact` is for the things whose whole meaning is WHERE they are. A corner unit is the
     * one: `findSpot` searches outward in 32mm steps when the wanted place is taken, which
     * is right for a sink and wrong for a corner -- it produced kitchens with a "900 corner"
     * sitting fourth of six along the back wall, priced as a corner unit, drawn as a box,
     * and solving nothing. Either it is in the corner or it is not a corner unit.
     */
    const spot = exact
      ? (findSpot(r, fixed.get(r.id), item.w, want, fromEnd, lim.minFiller) === round(want) ? round(want) : null)
      : findSpot(r, fixed.get(r.id), item.w, want, fromEnd, lim.minFiller)
    if (spot == null) { if (mustFit) return false; return true }
    fixed.get(r.id).push({ ...item, pos: spot })
    return true
  }

  /*
   * 1. A corner unit at every corner, before anything that can move.
   *
   * It used to go last, after the sink, the dishwasher, the cooktop and the fridge, and be
   * bumped along by `findSpot` when its own slot was taken -- which produced kitchens with a
   * "900 corner" fourth of six along the back wall: priced as a corner unit, drawn as a box,
   * and solving none of the problem a corner unit exists to solve.
   *
   * Its position is the whole of its meaning, so it is placed FIRST and exactly, and
   * everything with a choice about where it goes is placed around it.
   */
  if (S.corner) {
    for (const r of rails) {
      for (let k = 1; k < r.legs.length; k++) {
        const prev = r.legs[k - 1]
        place(r, { role: 'corner', specId: S.corner.id, w: S.corner.w }, prev.end - S.corner.w,
          { mustFit: false, exact: true })
      }
    }
  }

  // 2. The sink: the middle of the longest leg of the main rail.
  if (!has(main.id, 'sink') && S.sink) {
    const leg = main.legs.slice().sort((a, b) => b.len - a.len)[0]
    if (!place(main, { role: 'sink', specId: S.sink.id, w: S.sink.w }, leg.start + leg.len / 2 - S.sink.w / 2)) {
      return { error: 'nowhere to put a sink' }
    }
  }
  const sink = fixed.get(main.id).find((f) => f.role === 'sink')
  if (!sink) return { error: 'no sink in the range' }

  // 3. The dishwasher, hard against the sink on the side asked for.
  if (!has(main.id, 'dishwasher') && S.dishwasher) {
    const want = dishSide === 'left' ? sink.pos - S.dishwasher.w : sink.pos + sink.w
    const ok = place(main, { role: 'dishwasher', specId: S.dishwasher.id, w: S.dishwasher.w }, want, { mustFit: false })
    if (!ok) notes.push('no room for a dishwasher beside the sink')
  }
  const dish = fixed.get(main.id).find((f) => f.role === 'dishwasher')
  const dishOnLeft = dish ? dish.pos < sink.pos : dishSide === 'left'

  // 6. The cooktop: across a stretch of prep bench from the sink, on the side away from the
  //    dishwasher, never in a corner.
  if (!has(main.id, 'cooktop') && S.cooktop) {
    const prep = RULE.sinkSideMajor
    const away = dishOnLeft ? sink.pos + sink.w + prep : sink.pos - prep - S.cooktop.w
    const toward = dishOnLeft ? sink.pos - (dish ? dish.w : 0) - prep - S.cooktop.w : sink.pos + sink.w + (dish ? dish.w : 0) + prep
    const item = { role: 'cooktop', specId: S.cooktop.id, w: S.cooktop.w }
    if (!place(main, item, away, { fromEnd: RULE.cooktopFromWall, mustFit: false })
      || !has(main.id, 'cooktop')) {
      place(main, item, toward, { fromEnd: RULE.cooktopFromWall, mustFit: false })
    }
    if (!has(main.id, 'cooktop')) notes.push('no room for a cooktop with bench either side')
  }
  const cook = fixed.get(main.id).find((f) => f.role === 'cooktop')

  // 4. The fridge: at the end of the rail on the dishwasher's side, so the sink sits between
  //    fridge and cooktop -- the walk a designer draws first.
  if (!has(main.id, 'fridge') && S.fridge) {
    const item = { role: 'fridge', specId: S.fridge.id, w: S.fridge.w }
    const first = dishOnLeft ? 0 : main.length - S.fridge.w
    const second = dishOnLeft ? main.length - S.fridge.w : 0
    if (!place(main, item, first, { mustFit: false }) || !has(main.id, 'fridge')) {
      place(main, item, second, { mustFit: false })
    }
  }
  const fridge = fixed.get(main.id).find((f) => f.role === 'fridge')

  // 5. An oven tower beside the fridge, or a pantry there in the Classic mix; or nothing,
  //    and the oven lives under the cooktop.
  if (fridge) {
    const beside = fridge.pos < sink.pos ? fridge.pos + fridge.w : (w) => fridge.pos - w
    if (tower && S.oven && !has(main.id, 'oven')) {
      const w = S.oven.w
      place(main, { role: 'oven', specId: S.oven.id, w }, typeof beside === 'function' ? beside(w) : beside, { mustFit: false })
    } else if (mix.id === 'doors' && S.pantry) {
      const w = S.pantry.w
      place(main, { role: 'pantry', specId: S.pantry.id, w }, typeof beside === 'function' ? beside(w) : beside, { mustFit: false })
    }
  }

  /*
   * 7. Fill every leg of every rail around what is fixed -- and around the doorways.
   *
   * A doorway in the middle of a wall is a fixed thing that takes width and holds no
   * cabinet, which is the same shape as everything else the filler works around. `extents`
   * has already dealt with one at either END of a wall by shortening the run; what is left
   * for here is the one in the middle, which cannot be answered by moving a number because
   * a run is a contiguous list whose positions are implied by their order.
   *
   * A leg's own coordinates start at its run's `from`, so an opening's position along the
   * WALL has to be measured the same way or the gap lands somewhere else entirely.
   */
  const runsOut = []
  const stamp = { n: 0 }
  for (const r of rails) {
    const items = fixed.get(r.id).slice().sort((a, b) => a.pos - b.pos)
    for (const leg of r.legs) {
      const onLeg = items.filter((f) => f.pos >= leg.start - 0.5 && f.pos + f.w <= leg.end + 0.5)
      const filled = fillLeg(leg, onLeg, { mix, specs, lim, cook, sink, dish }, stamp)
      if (filled.error) return { error: `${leg.run.wallId} wall: ${filled.error}` }
      runsOut.push({ ...leg.run, items: filled })
    }
  }

  // 8. Overheads: a rangehood over the cooktop, nothing over a tall unit, cupboards elsewhere.
  for (const run of bones.runs.filter((x) => x.kind === 'wall')) {
    const base = runsOut.find((x) => x.kind === 'base' && x.wallId === run.wallId)
    /*
     * A corner unit at the END of the run that runs INTO a corner.
     *
     * Which end is not a choice: `extents` shortens the run on the far side of the corner,
     * so the near one runs all the way to the wall's end and the unit goes there. Only where
     * the next wall carries an overhead run of its own -- a run ending at a blank wall has
     * no corner to turn.
     */
    const w = room.segment(bones, run.wallId)
    const nextWall = w && !room.isIsland(bones, run.wallId) ? room.next(bones.walls, w) : null
    const turns = !!nextWall && bones.runs.some((x) => x.kind === 'wall' && x.wallId === nextWall.id)
    runsOut.push({ ...run, items: overheads(run, base,
      { specs, lim, hood: S.hood, corner: turns ? S.wallCorner : null,
        openings: room.blockedSpans(bones, run.wallId, 'wall', lim) }, stamp) })
  }

  const outPlan = { ...deepish(bonesToPlan(bones)), runs: runsOut }
  outPlan.locks = (fixedByLock).map((f) => ({ itemId: f.itemId, role: f.role }))
  return { plan: outPlan, notes }
}

/** The plan's non-run fields, copied. Walls are shared; nobody edits them in here. */
/**
 * The room an option is a design FOR, carried onto the option's own plan.
 *
 * The islands and the openings have to come with the walls: an option whose plan lost them
 * would draw a run standing on an island that is not there, and `Options.vue` renders each
 * option's plan on its own.
 */
function bonesToPlan(bones) {
  return { walls: bones.walls, islands: bones.islands ?? [], openings: bones.openings ?? [] }
}
function deepish(o) { return { ...o } }

/**
 * Somewhere on one leg for a cabinet this wide, as close as possible to where it was asked
 * for, clear of the ends by `fromEnd`, and not overlapping anything fixed. Tries the asked
 * spot, then either side of it in 32mm steps, then gives up.
 */
function findSpot(r, taken, w, want, fromEnd, minGap = 0) {
  const fits = (pos) => {
    const leg = legAt(r, pos)
    if (!leg) return false
    if (pos < leg.start + fromEnd - 0.5 || pos + w > leg.end - fromEnd + 0.5) return false
    /*
     * `clear` is space a fixed thing needs BESIDE it as well as under it.
     *
     * A doorway is the only thing that has any: a cooktop placed hard against one has no
     * bench to put a pan down on, and the review says so and rejects the whole design.
     * Without this the generator kept producing kitchens with the cooktop 180mm from the
     * door and then throwing them away, which reads to the person as "no options for this
     * room" when the truth is "not there".
     */
    if (taken.some((t) => {
      const c = t.clear ?? 0
      return pos < t.pos + t.w + c - 0.5 && pos + w > t.pos - c + 0.5
    })) return false

    /*
     * AND IT MAY NOT LEAVE A SLIVER BESIDE IT.
     *
     * `want` for a dishwasher is "hard against the sink", and a locked sink stands wherever
     * the plumbing put it -- 626mm along, say. That leaves 26mm between the dishwasher and
     * the end of the wall: too wide to ignore, too narrow for the maker's smallest filler,
     * and no cabinet stretches into it. `fillLeg` then spent its six passes shuffling the
     * dishwasher back and forth by 26mm -- back to close the sliver, forward again to meet
     * the sink it may not overlap -- and ended up laying it out ON the sink, which is a leg
     * that adds up to 26mm more than the wall and a design thrown away for arithmetic.
     *
     * A gap beside a cabinet is either nothing at all or wide enough for a filler. Asked
     * here, before the cabinet is placed, the whole class of sliver disappears: this
     * dishwasher simply has nowhere to go beside that sink, which is true, and `place` says
     * so in words instead ("no room for a dishwasher beside the sink").
     */
    if (minGap > 0) {
      const edges = [{ pos: leg.start, w: 0 }, { pos: leg.end, w: 0 }, ...taken]
      const before = Math.max(...edges.map((t) => (t.pos + t.w <= pos + 0.5 ? t.pos + t.w : -Infinity)))
      const after = Math.min(...edges.map((t) => (t.pos >= pos + w - 0.5 ? t.pos : Infinity)))
      const left = Number.isFinite(before) ? pos - before : 0
      const right = Number.isFinite(after) ? after - (pos + w) : 0
      if (left > 0.5 && left < minGap) return false
      if (right > 0.5 && right < minGap) return false
    }
    return true
  }
  const clampLeg = (pos) => {
    const leg = legAt(r, pos)
    return Math.min(Math.max(pos, leg.start + fromEnd), leg.end - fromEnd - w)
  }
  const start = clampLeg(want)
  if (fits(start)) return round(start)
  for (let d = 32; d < r.length; d += 32) {
    for (const p of [start - d, start + d]) {
      if (p < 0 || p + w > r.length) continue
      const c = clampLeg(p)
      if (fits(c)) return round(c)
    }
  }
  return null
}
const round = (n) => Math.round(n * 10) / 10

/**
 * One leg, filled around its fixed cabinets.
 *
 * Each gap between fixed things is filled with the mix's cabinets by the same `fill` that
 * fills a starter kitchen -- largest first, stretch, filler, or a stated problem. Gaps that
 * touch the cooktop get drawers whatever the mix, because that is where the pans live; the
 * gap beside the sink gets the bin drawer first. A gap too small to fill honestly nudges the
 * fixed cabinet on its right along, unless that cabinet is locked.
 */
function fillLeg(leg, fixedOnLeg, ctx, stamp) {
  const { mix, specs, lim, cook } = ctx
  const base = specs.filter((s) => s.kind === 'base' && !s.role)
  const drawerSet = base.filter((s) => s.drawers > 0)
  const doorSet = base.filter((s) => s.drawers === 0)
  const bin = base.find((s) => /bin/i.test(s.name))
  const mixSet = mix.id === 'drawers' ? [...drawerSet, ...doorSet.filter((s) => s.w <= 450)]
    : mix.id === 'doors' ? [...doorSet, ...drawerSet.filter((s) => s.w <= 450)]
    : base
  const specsFor = (gap) => {
    const touchesCook = cook && (Math.abs(gap.end - cook.pos) < 0.5 || Math.abs(gap.start - (cook.pos + cook.w)) < 0.5)
    return touchesCook ? [...drawerSet, ...doorSet.filter((s) => s.w <= 300)] : mixSet
  }
  const minGap = lim.minFiller ?? 30
  /*
   * A doorway in the middle of a wall is a fixed thing that takes width and holds no
   * cabinet, which is exactly the shape of everything else in `fixed`. So it goes in the
   * same list, and the filler between the fixed things flows around it without any of the
   * arithmetic below knowing what it is.
   *
   * It emits an item with `gap: true`, the marker the renderer, the plan, `faults`, the
   * schedule and the price all already skip -- the same one an overhead run uses to step
   * over a tall unit.
   */
  const fixed = fixedOnLeg.slice().sort((a, b) => a.pos - b.pos)

  /*
   * Close the slivers.
   *
   * Placing things in 32mm steps around cabinets locked at arbitrary positions leaves gaps
   * of 8mm and 16mm -- too wide to ignore, too narrow for a filler, and no cabinet stretches
   * that far. Any gap under the filler minimum is closed: the later cabinet slides back onto
   * the earlier one unless it is locked, in which case the earlier slides forward. Two locked
   * cabinets a sliver apart is the one case left, and it is reported as what it is.
   */
  const coalesce = () => {
    fixed.sort((a, b) => a.pos - b.pos)
    for (let i = 0; i < fixed.length; i++) {
      const a = i > 0 ? fixed[i - 1] : null
      const b = fixed[i]
      const gapBefore = a ? b.pos - (a.pos + a.w) : b.pos - leg.start
      if (gapBefore > 0.5 && gapBefore < minGap) {
        if (!b.itemId) b.pos -= gapBefore
        else if (a && !a.itemId) a.pos += gapBefore
      }
    }
    const last = fixed[fixed.length - 1]
    if (last) {
      const tail = leg.end - (last.pos + last.w)
      if (tail > 0.5 && tail < minGap && !last.itemId) last.pos += tail
    }
  }

  // Fill every gap. A gap that cannot be filled honestly nudges the fixed cabinet beside it
  // and the whole leg is tried again, because a nudge opens a sliver somewhere else. Four
  // passes is more than any real leg needs; the fifth failure is the answer.
  for (let pass = 0; pass < 6; pass++) {
    coalesce()
    const out = []
    let cursor = leg.start
    let nudged = false
    let error = null
    const bounds = [...fixed, null]   // null = the leg's end
    for (let i = 0; i < bounds.length && !nudged; i++) {
      const next = bounds[i]
      const gapEnd = next ? next.pos : leg.end
      const gap = { start: cursor, end: gapEnd }
      let len = gap.end - gap.start
      if (len > 0.5) {
        let seq = []
        // The bin drawer goes beside the sink, on whichever side the dishwasher is not.
        if (bin && ctx.sink && len >= bin.w + 0.5
          && (Math.abs(gap.end - ctx.sink.pos) < 0.5 || Math.abs(gap.start - (ctx.sink.pos + ctx.sink.w)) < 0.5)) {
          seq.push({ specId: bin.id, w: bin.w })
          len -= bin.w
        }
        const r = fill(len, specsFor(gap), lim)
        if (r.problem) {
          /*
           * A sliver nobody can fill. First choice: a fixed neighbour that the maker will cut
           * a little wider takes it -- a corner unit at 916 instead of 900 is a cabinet, not a
           * strip of board, and it is what a designer would draw. A locked cabinet may grow
           * away from its locked edge but never move. Failing that, push a neighbour along and
           * try the leg again.
           */
          const prev = i > 0 ? bounds[i - 1] : null
          const headroom = (f) => {
            const sp = specs.find((x) => x.id === f.specId)
            return sp ? Math.min(sp.stretch[1], lim.maxCabinetWidth) - f.w : 0
          }
          if (prev && headroom(prev) >= r.remainder - 0.01) {
            prev.w += r.remainder; prev.stretched = true; nudged = true; continue
          }
          if (next && !next.itemId && headroom(next) >= r.remainder - 0.01) {
            next.pos -= r.remainder; next.w += r.remainder; next.stretched = true; nudged = true; continue
          }
          /*
           * A push has to stay on the leg.
           *
           * These two used to move a neighbour by the remainder without asking whether the
           * leg had that much left, so a 26mm sliver could shove the last fixed cabinet 26mm
           * past the end of the wall. The next pass then laid it out at its full width with a
           * negative gap after it, which `sum` cannot see and the check at the bottom reports
           * as "adds up to 7595 on a 7569 leg" -- an arithmetic message for what is really a
           * cabinet standing off the end of the room.
           */
          const after = bounds[i + 1]
          const ceiling = after ? after.pos : leg.end
          const twoBack = i > 1 ? bounds[i - 2] : null
          const floor = twoBack ? twoBack.pos + twoBack.w : leg.start
          if (next && !next.itemId && next.pos + next.w + r.remainder <= ceiling + 0.5) {
            next.pos += r.remainder; nudged = true; continue
          }
          if (prev && !prev.itemId && prev.pos - r.remainder >= floor - 0.5) {
            prev.pos -= r.remainder; nudged = true; continue
          }
          error = `a ${Math.round(len)}mm gap ${r.problem}`
          break
        }
        seq = seq.concat(r.items)
        // Beside the sink the bin comes first; beside the cooktop the drawers do. Reversing
        // a gap that ends at the cooktop puts the widest drawer bank next to it.
        if (cook && Math.abs(gap.end - cook.pos) < 0.5) seq = seq.slice().reverse()
        for (const it of seq) out.push({ ...it, id: `${leg.run.id}-d${stamp.n++}` })
      }
      if (next) {
        out.push({ id: next.itemId ?? `${leg.run.id}-d${stamp.n++}`, specId: next.specId, w: next.w,
          ...(next.gap ? { gap: true } : {}),
          ...(next.stretched ? { stretched: true } : {}) })
        cursor = next.pos + next.w
      }
    }
    if (nudged) continue
    if (error) return { error }
    const total = sum(out)
    if (Math.abs(total - leg.len) > 0.5) return { error: `adds up to ${Math.round(total)} on a ${Math.round(leg.len)} leg` }
    return out
  }
  return { error: 'could not settle the gaps around what is fixed' }
}

/**
 * The overhead run above a base run.
 *
 * Built separately from the base run rather than through `fillLeg`, because what is fixed
 * up here is decided by what is DOWN there: a rangehood over the cooktop, and nothing at
 * all over a tall unit. Which means every fixed thing has to be added here too -- and the
 * openings were not, so the cupboards walked straight across the doorway while the
 * cabinets under them stepped neatly around it.
 */
function overheads(run, base, { specs, lim, hood, corner, openings = [] }, stamp) {
  const len = run.to - run.from
  const walls = specs.filter((s) => s.kind === 'wall' && !s.role)
  const fixed = []
  // The corner unit first, at the far end, so everything else is laid out around it rather
  // than into the space it needs -- the same reasoning as the base run's, which places it
  // before anything that can move.
  if (corner && len >= corner.w + 200) fixed.push({ specId: corner.id, w: corner.w, pos: len - corner.w })
  // A door goes to the ceiling; a window at bench height is in the way up here even though
  // it was not down there. `blocks` decides which, on the same numbers the base run used.
  for (const o of openings) fixed.push({ gap: true, w: o.to - o.from, pos: o.from - run.from })
  if (base) {
    let off = base.from
    for (const it of base.items) {
      const spec = specs.find((s) => s.id === it.specId)
      const wallPos = off - run.from   // along this wall run, from its start
      if (spec?.role === 'cooktop' && hood) {
        const w = Math.min(hood.stretch?.[1] ?? hood.w, Math.max(hood.stretch?.[0] ?? hood.w, it.w))
        fixed.push({ specId: hood.id, w, pos: wallPos + (it.w - w) / 2 })
      } else if (spec?.kind === 'tall') {
        // Nothing above a tall unit: it is already full height.
        fixed.push({ gap: true, w: it.w, pos: wallPos })
      }
      off += it.w
    }
  }
  const sorted = fixed.filter((f) => f.pos + f.w > 0.5 && f.pos < len - 0.5).sort((a, b) => a.pos - b.pos)
  const out = []
  let cursor = 0
  for (const f of [...sorted, null]) {
    const end = f ? Math.max(cursor, f.pos) : len
    const gapLen = end - cursor
    if (gapLen > 0.5) {
      const r = fill(gapLen, walls, lim)
      const items = r.problem && r.remainder > 0
        ? [...r.items, { specId: 'filler', w: r.remainder, filler: true }]
        : r.items
      for (const it of items) out.push({ ...it, id: `${run.id}-d${stamp.n++}` })
    }
    if (f) {
      const w = Math.min(f.w, len - end)
      if (f.gap) out.push({ id: `${run.id}-d${stamp.n++}`, specId: 'gap', w, gap: true })
      else out.push({ id: `${run.id}-d${stamp.n++}`, specId: f.specId, w })
      cursor = end + w
    }
  }
  return out
}

// ── never an empty room ────────────────────────────────────────────────────────────

/**
 * Design it for me, and if it cannot be designed, at least FILL it.
 *
 * WHY THIS EXISTS. `design()` answers "here are three kitchens, all valid by construction",
 * and when it cannot it answers with nothing, which is correct and useless. The page's
 * "New design from this room" takes the Original, empties it down to the locked services and
 * hands that to `design()` -- so when `design()` found nothing the person was left looking at
 * a room containing one base cabinet and a cooktop, having just pressed a button whose only
 * job was to put a kitchen in it. Their words: "it refused to put cabinets in even though
 * there was clearly space for it". There WAS space. Nothing was standing in it.
 *
 * Some lock sets are genuinely impossible -- an oven tower hard against a cooktop leaves no
 * bench beside the cooktop, and that is a real kitchen you would not build -- so the answer
 * is not to lower the bar. It is to say what had to give:
 *
 *   1. Every lock honoured: three kitchens, and `dropped` is empty.
 *   2. Otherwise, let one lock go at a time, most constraining first, and say which. A
 *      kitchen with the oven moved is a far better answer than no kitchen.
 *   3. Otherwise, fill every run with the maker's range, the same fill a tapped wall gets.
 *      Not a designed kitchen and not offered as one, but a room with cupboards in it that
 *      somebody can then drag about -- which is where they were going anyway.
 *
 * @returns {{ options, rejected, impossible, dropped: Array<{itemId, role}>, filled: boolean,
 *             note: string|null }}  `note` is one sentence for the sheet to show.
 */
export function designOrFill(plan, maker, opts = {}) {
  const first = design(plan, maker, opts)
  if (first.options.length) return { ...first, dropped: [], filled: false, note: null }

  const locks = plan.locks ?? []
  const ranked = rankLocks(plan, maker, locks, first.impossible ?? [])
  const gone = []
  for (const l of ranked) {
    gone.push(l)
    const kept = locks.filter((x) => !gone.some((d) => d.itemId === x.itemId))
    const tried = design({ ...plan, locks: kept }, maker, opts)
    if (tried.options.length) {
      return { ...tried, dropped: gone.slice(), filled: false, note: letGo(gone) }
    }
  }

  const filled = fillRuns(plan, maker)
  return {
    options: [{
      id: 'filled',
      name: 'The room, filled',
      blurb: 'Cupboards from the range, wall to wall, for you to move about',
      why: [
        locks.length
          ? 'No arrangement kept the things you locked, so this one keeps none of them'
          : 'The generator found no kitchen for this room, so the runs are simply filled',
        'Drag a cabinet, or tap one to swap it, and the run stays exactly full',
      ],
      plan: filled,
      score: 0,
    }],
    rejected: first.rejected ?? [],
    impossible: first.impossible ?? [],
    dropped: locks.slice(),
    filled: true,
    note: locks.length
      ? `Nothing fitted with ${listRoles(locks)} where ${locks.length > 1 ? 'they are' : 'it is'}, `
        + 'so the runs were filled from the range instead. Nothing is locked in what you see.'
      : 'No arrangement passed the review, so the runs were filled from the range instead.',
  }
}

/** "the oven was let go", for the sheet. */
const letGo = (gone) => `Nothing fitted with everything locked, so ${listRoles(gone)} `
  + `${gone.length > 1 ? 'were' : 'was'} let go. The rest stayed where ${gone.length > 1 ? 'they are' : 'it is'}.`

const listRoles = (list) => {
  const words = list.map((l) => `the ${l.role ?? 'locked cabinet'}`)
  return words.length <= 1 ? (words[0] ?? 'nothing')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

/**
 * Which lock to let go of first: the one that costs the most to keep.
 *
 * Named impossible by the generator first -- it has already said that one cannot be honoured
 * at all. Then the tall units, because a full-height oven tower or fridge is what leaves a
 * cooktop with no bench beside it and a corner with nothing that will turn it, which is the
 * fault that rejects most of the variants. Then the widest, because width is what a leg runs
 * out of. Deterministic, so the same room always gives up the same thing.
 */
function rankLocks(plan, maker, locks, impossible) {
  const where = new Map()
  for (const run of plan.runs ?? []) {
    for (const it of run.items ?? []) where.set(it.id, it)
  }
  const cost = (l) => {
    const it = where.get(l.itemId)
    const spec = maker.cabinets.find((c) => c.id === it?.specId)
    return (impossible.some((i) => i.itemId === l.itemId) ? 100000 : 0)
      + (spec?.kind === 'tall' ? 10000 : 0)
      + (it?.w ?? 0)
  }
  return locks.slice().sort((a, b) => cost(b) - cost(a)
    || String(a.itemId).localeCompare(String(b.itemId)))
}

/**
 * Every run in the plan, filled with the maker's range -- the same fill a tapped wall gets.
 *
 * `scene.js` does this for a run with no items in it, which is how a person gets cupboards by
 * tapping a wall. This is the same thing done to a whole plan, off the renderer, so the
 * generator can fall back to it and a test can check it. `compose` rather than `fill`,
 * because a room that has just been filled should look like a kitchen -- a sink and a
 * cooktop in it -- rather than a row of identical boxes; `taken` is shared across the runs so
 * the room gets one of each and not one per wall.
 *
 * The doorways are stepped over exactly as they are everywhere else: a merged blocked span
 * becomes a `gap` item, so the run is still exactly as long as its extent and no cupboard
 * stands in the door.
 */
export function fillRuns(plan, maker) {
  const lim = maker.limits
  const specs = maker.cabinets
  const bones = {
    walls: plan.walls,
    islands: plan.islands ?? [],
    openings: plan.openings ?? [],
    runs: (plan.runs ?? []).map((r) => ({ id: r.id, wallId: r.wallId, kind: r.kind, items: [] })),
  }
  const ext = room.extents(bones, lim)
  const taken = new Set()
  const stamp = { n: 0 }
  const runs = bones.runs.map((run) => {
    const e = ext[run.id] ?? { from: 0, to: 0 }
    const kinds = specs.filter((s) => s.kind === run.kind && s.role !== 'corner')
    const items = []
    const push = (it) => items.push({ ...it, id: `${run.id}-f${stamp.n++}` })
    // The stretches between the doorways, in order, and the doorways themselves.
    const parts = []
    let cursor = e.from
    for (const o of room.blockedSpans(bones, run.wallId, run.kind, lim)) {
      const a = Math.max(e.from, o.from), b = Math.min(e.to, o.to)
      if (b - a < 1) continue
      parts.push({ from: cursor, to: a, hole: false })
      parts.push({ from: a, to: b, hole: true })
      cursor = b
    }
    parts.push({ from: cursor, to: e.to, hole: false })
    for (const p of parts) {
      const len = p.to - p.from
      if (len < 0.5) continue
      if (p.hole) { push({ specId: 'gap', gap: true, w: len }); continue }
      const r = kinds.length ? compose(len, kinds, { ...lim, taken }) : { items: [], remainder: len }
      for (const it of r.items) push(it)
      // `compose` says what it could not place; a run is exactly full or it is not a run.
      const left = len - sum(r.items)
      if (left > 0.5) push({ specId: 'filler', w: left, filler: true })
    }
    return { ...run, items }
  })
  return { ...plan, runs, locks: [] }
}

// ── scoring ────────────────────────────────────────────────────────────────────────

/**
 * What a designer would say for and against, as a number and as sentences.
 *
 * The review's faults disqualify; its advice costs. Then the things a review does not
 * check because they are preferences rather than rules: the walk between fridge, sink and
 * cooktop; drawers where the cooking happens; enough drawer frontage overall.
 */
function score(plan, maker, notes) {
  const findings = review(plan, maker)
  const faults = findings.filter((f) => f.level === 'fault')
  let s = 0
  const why = []
  s -= findings.filter((f) => f.level === 'advice').length * 10

  const base = plan.runs.filter((r) => r.kind === 'base')
  const placed = []
  for (const r of base) {
    let off = 0
    for (const it of r.items) {
      const spec = maker.cabinets.find((c) => c.id === it.specId)
      placed.push({ run: r, it, spec, role: spec?.role ?? null, start: off, end: off + it.w })
      off += it.w
    }
  }
  const find = (role) => placed.find((p) => p.role === role)
  const sink = find('sink'), cook = find('cooktop'), dish = find('dishwasher'), fridge = find('fridge')

  if (sink && dish && sink.run === dish.run && (Math.abs(dish.end - sink.start) < 0.5 || Math.abs(sink.end - dish.start) < 0.5)) {
    s += 15; why.push(`Dishwasher ${dish.start < sink.start ? 'left' : 'right'} of the sink, where the plates go`)
  }
  if (sink && cook && sink.run === cook.run) {
    const gap = cook.start > sink.start ? cook.start - sink.end : sink.start - cook.end
    if (gap >= RULE.sinkSideMajor) { s += 15; why.push(`${Math.round(gap)}mm of prep bench between sink and cooktop`) }
  }
  if (cook) {
    const drawersNear = placed.filter((p) => p.run === cook.run && p.spec?.drawers > 0 && !p.role
      && (Math.abs(p.end - cook.start) < 900 || Math.abs(p.start - cook.end) < 900)).length
    if (drawersNear) { s += Math.min(15, drawersNear * 5); why.push(`Drawers ${drawersNear > 1 ? 'either side of' : 'beside'} the cooktop for pans`) }
  }
  if (fridge && sink && cook) {
    const between = (fridge.start < sink.start && sink.start < cook.start) || (cook.start < sink.start && sink.start < fridge.start)
    if (between && fridge.run === sink.run) { s += 10; why.push('Fridge, sink, cooktop in that order — the short walk') }
  }
  const corners = placed.filter((p) => p.role === 'corner').length
  if (corners) { s += corners * 5; why.push(`${corners === 1 ? 'A corner unit' : corners + ' corner units'} so nothing is lost in the corner`) }
  const frontage = placed.filter((p) => !p.role && !p.it.filler).reduce((n, p) => n + p.it.w, 0) || 1
  const drawerFrontage = placed.filter((p) => !p.role && p.spec?.drawers > 0).reduce((n, p) => n + p.it.w, 0)
  const ratio = drawerFrontage / frontage
  if (ratio >= 0.4 && ratio <= 0.7) s += 10
  // What makes THIS one this one goes first: three cards that all open with "dishwasher
  // left of the sink" read as the same kitchen three times, and they are not.
  const pantry = placed.some((p) => p.spec?.kind === 'tall' && !p.role)
  const oven = placed.some((p) => p.role === 'oven')
  const lead = ratio > 0.7 ? 'Mostly drawers — everything within reach, the dearest to build'
    : ratio < 0.3 ? 'Mostly cupboards — the least expensive way to fill a wall'
    : 'Drawers where they earn it, cupboards where they do not'
  const second = oven ? 'An oven tower beside the fridge, at eye height'
    : pantry ? 'A pantry beside the fridge'
    : 'Oven under the cooktop, so the bench runs unbroken'
  for (const n of notes) why.push(n)
  return { score: s, faults, why: [lead, second, ...why].slice(0, 4) }
}
